<template>
  <div id="listWidget" class="mb-4" style="padding-bottom: 7rem;">
    <!-- <h4 class="mb-3">프로필</h4> -->

    <!-- 개인정보 카드 -->
    <div class="info-card card-item mb-3">
      <div class="info-card-header">
        <i class="fa-regular fa-user me-2 text-primary"></i>
        <span class="info-card-title">개인정보</span>
      </div>
      <div class="info-card-body">
        <!-- 닉네임 (첫번째 줄) -->
        <div class="info-item">
          <div class="info-label">닉네임</div>
          <div class="info-value-wrapper">
            <div class="info-value">{{ profile?.nickName || '' }}</div>
            <div class="info-action" @click="onEditNicknameClick">
              <i class="fa-regular fa-pen-to-square text-muted"></i>
            </div>
          </div>
        </div>

        <!-- 이메일 (두번째 줄) -->
        <div class="info-item" v-if="profile?.email">
          <div class="info-label">이메일</div>
          <div class="info-value">{{ profile?.email || '' }}</div>
        </div>
      </div>
    </div>

    <!-- 공유 카드 -->
    <div class="info-card card-item mb-3">
      <div class="info-card-header">
        <i class="fa-regular fa-share-from-square me-2 text-primary"></i>
        <span class="info-card-title">공유</span>
      </div>
      <div class="info-card-body">
        <div class="info-item">
          <div class="info-label">공유 코드</div>
          <div class="info-value-wrapper">
            <div class="info-value">{{ profile?.shareCode || '' }}</div>
            <div class="info-action" @click.prevent="onCopyShareCodeClick">
              <input
                ref="clipboardElem"
                type="hidden"
                :value="profile?.shareCode || ''"
              />
              <i class="fa-regular fa-copy text-muted"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 로그아웃/회원탈퇴 버튼 (하단 고정) -->
  <div class="action-button-container-fixed" v-if="getters.isLogin">
    <button
      type="button"
      class="btn btn-danger w-100 mb-2"
      @click="onLogoutClick"
    >
      <i class="fa fa-sign-out me-2"></i>
      로그아웃
    </button>
    <button
      type="button"
      class="btn btn-default w-100"
      @click="onDeleteMemberClick"
    >
      회원탈퇴
    </button>
  </div>

  <NicknameModal
    v-if="nicknameModal.visible"
    :visible="nicknameModal.visible"
    :okCallback="onUpdateNicknameClick"
    :cancelCallback="nicknameModal.hide"
    :nickName="profile.nickName"
  />
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onMounted,
  computed,
  onBeforeMount,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import Axios, { AuthApi } from '@/api';
import NicknameModal from './components/NicknameModal.vue';
import useModal from '@/common/hooks/useModal';

const store = useStore();
const getters = computed(() => store.getters);
const profile = computed(() => getters.value.account);
const router = useRouter();
const { emitter, mobile } = getCurrentInstance().appContext.config.globalProperties;
const nicknameModal = reactive(useModal());
const clipboardElem = ref(null);

onBeforeMount(() => {
  emitter.emit('onChangeHeaderSetting', { title: '프로필', isShowZone: false, isShowMenu: false, isShowRightHome: false, isShowProfile: false });
});
onBeforeUnmount(() => {
  emitter.emit('onChangeHeaderSetting', { title: '', isShowZone: true, isShowMenu: true, isShowRightHome: false, isShowProfile: true });
});

const onCopyShareCodeClick = () => {
  clipboardElem.value.setAttribute('type', 'text');
  clipboardElem.value.select();
  document.execCommand('copy');
  clipboardElem.value.setAttribute('type', 'hidden');

  emitter.emit('showPopToast', {
    description: '공유코드가 클립보드에 저장되었습니다.',
    type: 'success',
  });
};

const onEditNicknameClick = () => {
  nicknameModal.show();
};

const onUpdateNicknameClick = (value) => {
  requestUpdateAccount({
    nickName: value,
  });
  nicknameModal.hide();
};

const onDeleteMemberClick = () => {
  router.push("/delete/user");
};

const onLogoutClick = () => {
  emitter.emit('showPopConfirm', {
    title: '로그아웃',
    description: `정말 로그아웃 하시겠습니까?`,
    okCallback: () => {
      emitter.on('onLogoutResult', () => {
        emitter.off('onLogoutResult');
      });
      mobile.logout(JSON.stringify({ callback: 'onLogoutResult' }));
      store.dispatch('logout');
      router.push('/login');
    },
  });
};

const requestUpdateAccount = async (params) => {
  try {
    const result = await Axios.post(AuthApi.UPDATE_ACCOUNT, params, 'auth');
    if (result.resultCode === '0000') {
      if (result.resultData.nickName) {
        store.commit('setNickname', result.resultData.nickName);
      }
    } else {
      emitter.emit('showPopAlert', {
        description: result.resultMessage,
      });
    }
  } catch (error) {
    emitter.emit('showPopAlert', {
      description: '프로필 수정에 실패했습니다.',
    });
  }
};
</script>

<style scoped>
/* card-item 공통 스타일 */
.card-item {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.card-item:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

/* info-card 스타일 */
.info-card-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.info-card-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.info-card-body {
  padding: 1rem;
}

.info-item {
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.info-item:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.info-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #212529;
  word-break: break-word;
}

.info-value-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

.info-value-wrapper .info-value {
  flex: 1;
}

.info-action {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: all 0.2s ease;
}

.info-action:hover {
  transform: scale(1.1);
}

.info-action i {
  font-size: 1rem;
}

/* 하단 고정 버튼 스타일 */
.action-button-container-fixed {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1rem;
  background-color: transparent;
  border-top: none;
  box-shadow: none;
  z-index: 1000;
}

/* PC(사이드바 존재)에서는 콘텐츠 영역 폭에 맞춘다 */
@media (min-width: 992px) {
  .action-button-container-fixed {
    left: 240px;
    right: 0;
  }

  /* 사이드바가 접힌(미니파이) 경우 */
  :global(.app-sidebar-minified) .action-button-container-fixed {
    left: 65px;
  }

  /* 사이드바 숨김(콘텐츠 full width) 경우 */
  :global(.app-content-full-width) .action-button-container-fixed,
  :global(.app-without-sidebar) .action-button-container-fixed {
    left: 0;
  }
}

.action-button-container-fixed .btn {
  padding: 0.75rem 1.25rem;
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.action-button-container-fixed .btn:hover:not(:disabled) {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transform: translateY(-1px);
}

.action-button-container-fixed .btn:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.action-button-container-fixed .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 모바일 반응형 간격 조정 */
@media (max-width: 768px) {
  #listWidget {
    margin-bottom: 1rem;
    padding-bottom: 7rem;
  }

  .info-card {
    margin-bottom: 1rem;
  }

  .action-button-container-fixed {
    padding: 0.875rem;
  }

  .action-button-container-fixed .btn {
    padding: 0.625rem 1rem;
    font-size: 0.9rem;
  }
}
</style>
